import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DateCell } from '../components/DateCell';
import { fetchMrvDraft } from '../features/ai/aiApi';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { InputsSummaryCharts } from '../features/managementReview/InputsSummaryCharts';
import {
  addParticipant,
  createDecision,
  decisionToNc,
  decisionToObjective,
  fetchManagementReview,
  removeParticipant,
  updateDecision,
  updateManagementReview,
} from '../features/managementReview/managementReviewApi';
import type {
  ManagementReviewDecision,
  ManagementReviewDetail,
  MgmtDecisionStatus,
  MgmtDecisionType,
  MgmtReviewStatus,
} from '../features/managementReview/types';
import {
  DECISION_STATUS_LABEL,
  DECISION_TYPE_LABEL,
  REVIEW_STATUS_LABEL,
  decisionStatusBadgeClass,
  formatPeriodLabel,
  reviewStatusBadgeClass,
} from '../features/managementReview/managementReviewUtils';
import { ExportPdfButton } from '../features/reports/ExportPdfButton';
import { fetchUsers } from '../features/users/usersApi';

const DECISION_TYPES = Object.keys(DECISION_TYPE_LABEL) as MgmtDecisionType[];
const DECISION_STATUSES = Object.keys(DECISION_STATUS_LABEL) as MgmtDecisionStatus[];
const REVIEW_STATUSES = Object.keys(REVIEW_STATUS_LABEL) as MgmtReviewStatus[];

export function ManagementReviewDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { canEdit } = usePlatformAccessLevel('management_review.access');
  const [review, setReview] = useState<ManagementReviewDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [conclusions, setConclusions] = useState('');
  const [status, setStatus] = useState<MgmtReviewStatus>('borrador');
  const [participantUserId, setParticipantUserId] = useState('');
  const [users, setUsers] = useState<Array<{ id: number; email: string }>>([]);
  const [decisionTopic, setDecisionTopic] = useState('');
  const [decisionDescription, setDecisionDescription] = useState('');
  const [decisionType, setDecisionType] = useState<MgmtDecisionType>('mejora');
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [aiDraftNote, setAiDraftNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      const data = await fetchManagementReview(id);
      setReview(data);
      setConclusions(data.conclusions ?? '');
      setStatus(data.status);
    } catch {
      setError('No se pudo cargar el acta.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const replaceDecision = useCallback((updated: ManagementReviewDecision) => {
    setReview((prev) =>
      prev
        ? {
            ...prev,
            decisions: prev.decisions.map((decision) =>
              decision.id === updated.id ? updated : decision,
            ),
          }
        : prev,
    );
  }, []);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  useEffect(() => {
    if (!canEdit) return;
    void fetchUsers({ pageSize: 200 })
      .then((res) => setUsers(res.data.map((u) => ({ id: u.id, email: u.email }))))
      .catch(() => undefined);
  }, [canEdit]);

  const handleSaveReview = async () => {
    if (!id) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateManagementReview(id, { conclusions, status });
      setReview((prev) =>
        prev
          ? {
              ...prev,
              conclusions: updated.conclusions ?? conclusions,
              status: updated.status ?? status,
            }
          : prev,
      );
      setStatus(updated.status ?? status);
    } catch {
      setError('No se pudo guardar el acta.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddParticipant = async () => {
    if (!id || !participantUserId) return;
    setError(null);
    try {
      const participant = await addParticipant(id, Number(participantUserId));
      setReview((prev) =>
        prev ? { ...prev, participants: [...prev.participants, participant] } : prev,
      );
      setParticipantUserId('');
    } catch {
      setError('No se pudo agregar el participante.');
    }
  };

  const handleRemoveParticipant = async (participantId: string) => {
    if (!id) return;
    setError(null);
    try {
      await removeParticipant(id, participantId);
      setReview((prev) =>
        prev
          ? {
              ...prev,
              participants: prev.participants.filter(
                (participant) => participant.id !== participantId,
              ),
            }
          : prev,
      );
    } catch {
      setError('No se pudo quitar el participante.');
    }
  };

  const handleCreateDecision = async () => {
    if (!id || !decisionTopic.trim() || !decisionDescription.trim()) return;
    setError(null);
    try {
      const decision = await createDecision(id, {
        topic: decisionTopic,
        description: decisionDescription,
        type: decisionType,
      });
      setReview((prev) =>
        prev ? { ...prev, decisions: [...prev.decisions, decision] } : prev,
      );
      setDecisionTopic('');
      setDecisionDescription('');
    } catch {
      setError('No se pudo crear la decisión.');
    }
  };

  const handleDecisionStatus = async (decisionId: string, newStatus: MgmtDecisionStatus) => {
    setError(null);
    try {
      const updated = await updateDecision(decisionId, { status: newStatus });
      replaceDecision(updated);
    } catch {
      setError('No se pudo actualizar la decisión.');
    }
  };

  const handleToObjective = async (decisionId: string) => {
    setError(null);
    try {
      const result = await decisionToObjective(decisionId);
      if (result.decision) {
        replaceDecision(result.decision);
      } else {
        setReview((prev) =>
          prev
            ? {
                ...prev,
                decisions: prev.decisions.map((decision) =>
                  decision.id === decisionId
                    ? { ...decision, objectiveId: result.objectiveId }
                    : decision,
                ),
              }
            : prev,
        );
      }
    } catch {
      setError('No se pudo generar el objetivo.');
    }
  };

  const handleToNc = async (decisionId: string) => {
    setError(null);
    try {
      const result = await decisionToNc(decisionId);
      if (result.decision) {
        replaceDecision(result.decision);
      } else {
        setReview((prev) =>
          prev
            ? {
                ...prev,
                decisions: prev.decisions.map((decision) =>
                  decision.id === decisionId
                    ? { ...decision, qmsRecordId: result.qmsRecordId }
                    : decision,
                ),
              }
            : prev,
        );
      }
    } catch {
      setError('No se pudo generar la no conformidad.');
    }
  };

  const participantUserIds = useMemo(
    () => new Set((review?.participants ?? []).map((participant) => participant.userId)),
    [review?.participants],
  );

  const availableUsers = useMemo(
    () => users.filter((user) => !participantUserIds.has(user.id)),
    [users, participantUserIds],
  );

  useEffect(() => {
    if (participantUserId && participantUserIds.has(Number(participantUserId))) {
      setParticipantUserId('');
    }
  }, [participantUserId, participantUserIds]);

  if (loading && !review) {
    return <div className="p-6 text-muted-foreground">Cargando acta…</div>;
  }

  if (!review) {
    return <div className="p-6 text-destructive">{error ?? 'Acta no encontrada'}</div>;
  }

  const isClosed = review.status === 'cerrada';

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/revision-direccion" className="text-sm text-muted-foreground hover:underline">
            ← Volver al listado
          </Link>
          <h1 className="text-2xl font-bold mt-2">{review.title}</h1>
          <p className="text-sm text-muted-foreground">
            Período: {formatPeriodLabel(review.periodStart, review.periodEnd)} · Revisión:{' '}
            <DateCell value={review.reviewDate} />
          </p>
          <span
            className={`inline-flex mt-2 px-2 py-0.5 rounded text-xs font-medium ${reviewStatusBadgeClass(review.status)}`}
          >
            {REVIEW_STATUS_LABEL[review.status]}
          </span>
        </div>
        {id && (
          <ExportPdfButton
            reportType="management_review"
            resourceId={id}
            permissionKey="management_review.access"
          />
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Entradas (snapshot congelado)</h2>
        <InputsSummaryCharts inputs={review.inputsSnapshot} />
      </section>

      <section className="bg-card border border-border rounded-lg p-4 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold">Conclusiones y cierre</h2>
          {canEdit && !isClosed && id && (
            <button
              type="button"
              disabled={drafting}
              className="rounded-md border border-primary px-3 py-1.5 text-sm text-primary disabled:opacity-50"
              onClick={async () => {
                setDrafting(true);
                setAiDraftNote(null);
                try {
                  const result = await fetchMrvDraft({ reviewId: id });
                  const narrative =
                    (result.draft.narrative as string | undefined) ??
                    (result.draft.summary as string | undefined) ??
                    result.text;
                  if (narrative.trim()) {
                    setConclusions(narrative.trim());
                    setAiDraftNote('Borrador IA cargado — revise antes de guardar.');
                  }
                } catch (err: unknown) {
                  const message =
                    err && typeof err === 'object' && 'response' in err
                      ? (err as { response?: { data?: { message?: string } } }).response?.data
                          ?.message
                      : null;
                  setError(message ?? 'No se pudo generar el borrador IA.');
                } finally {
                  setDrafting(false);
                }
              }}
            >
              {drafting ? 'Generando borrador…' : 'Borrador IA'}
            </button>
          )}
        </div>
        {aiDraftNote && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
            {aiDraftNote}
          </p>
        )}
        <textarea
          className="input w-full min-h-[120px]"
          value={conclusions}
          onChange={(e) => setConclusions(e.target.value)}
          disabled={!canEdit || isClosed}
          maxLength={10000}
        />
        {canEdit && !isClosed && (
          <div className="flex flex-wrap gap-3 items-center">
            <select
              className="input"
              value={status}
              onChange={(e) => setStatus(e.target.value as MgmtReviewStatus)}
            >
              {REVIEW_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {REVIEW_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-primary" onClick={() => void handleSaveReview()} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar acta'}
            </button>
          </div>
        )}
      </section>

      <section className="bg-card border border-border rounded-lg p-4 space-y-4">
        <h2 className="text-lg font-semibold">Participantes</h2>
        <ul className="space-y-2">
          {review.participants.map((participant) => (
            <li key={participant.id} className="flex items-center justify-between gap-2 text-sm">
              <span>
                {participant.user.email} {participant.attended ? '(asistió)' : '(ausente)'}
              </span>
              {canEdit && !isClosed && (
                <button
                  type="button"
                  className="text-destructive hover:underline"
                  onClick={() => void handleRemoveParticipant(participant.id)}
                >
                  Quitar
                </button>
              )}
            </li>
          ))}
          {review.participants.length === 0 && (
            <li className="text-sm text-muted-foreground">Sin participantes registrados</li>
          )}
        </ul>
        {canEdit && !isClosed && (
          <div className="flex flex-wrap gap-2">
            <select
              className="input"
              value={participantUserId}
              onChange={(e) => setParticipantUserId(e.target.value)}
              disabled={availableUsers.length === 0}
            >
              <option value="">
                {availableUsers.length === 0
                  ? 'No hay más usuarios disponibles'
                  : 'Seleccionar usuario…'}
              </option>
              {availableUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.email}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={!participantUserId}
              onClick={() => void handleAddParticipant()}
            >
              Agregar
            </button>
          </div>
        )}
      </section>

      <section className="bg-card border border-border rounded-lg p-4 space-y-4">
        <h2 className="text-lg font-semibold">Decisiones</h2>
        <div className="space-y-3">
          {review.decisions.map((decision) => (
            <article key={decision.id} className="border border-border rounded-lg p-3 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-medium">{decision.topic}</h3>
                <span
                  className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${decisionStatusBadgeClass(decision.status)}`}
                >
                  {DECISION_STATUS_LABEL[decision.status]}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">{decision.description}</p>
              <p className="text-xs text-muted-foreground">
                Tipo: {DECISION_TYPE_LABEL[decision.type]}
                {decision.objectiveId && (
                  <>
                    {' '}
                    · Objetivo:{' '}
                    <Link to={`/objetivos/${decision.objectiveId}`} className="underline">
                      ver
                    </Link>
                  </>
                )}
                {decision.qmsRecordId && (
                  <>
                    {' '}
                    · NC:{' '}
                    <Link
                      to={`/registros/no-conformidades/${decision.qmsRecordId}`}
                      className="underline"
                    >
                      ver
                    </Link>
                  </>
                )}
              </p>
              {canEdit && !isClosed && (
                <div className="flex flex-wrap gap-2">
                  <select
                    className="input text-sm"
                    value={decision.status}
                    onChange={(e) =>
                      void handleDecisionStatus(decision.id, e.target.value as MgmtDecisionStatus)
                    }
                  >
                    {DECISION_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {DECISION_STATUS_LABEL[s]}
                      </option>
                    ))}
                  </select>
                  {!decision.objectiveId && (
                    <button
                      type="button"
                      className="btn btn-secondary text-sm"
                      onClick={() => void handleToObjective(decision.id)}
                    >
                      Generar objetivo
                    </button>
                  )}
                  {!decision.qmsRecordId && (
                    <button
                      type="button"
                      className="btn btn-secondary text-sm"
                      onClick={() => void handleToNc(decision.id)}
                    >
                      Generar NC
                    </button>
                  )}
                </div>
              )}
            </article>
          ))}
        </div>

        {canEdit && !isClosed && (
          <div className="border-t border-border pt-4 space-y-3">
            <h3 className="font-medium text-sm">Nueva decisión</h3>
            <input
              className="input w-full"
              placeholder="Tema"
              value={decisionTopic}
              onChange={(e) => setDecisionTopic(e.target.value)}
              maxLength={255}
            />
            <textarea
              className="input w-full min-h-[80px]"
              placeholder="Descripción"
              value={decisionDescription}
              onChange={(e) => setDecisionDescription(e.target.value)}
              maxLength={10000}
            />
            <select
              className="input"
              value={decisionType}
              onChange={(e) => setDecisionType(e.target.value as MgmtDecisionType)}
            >
              {DECISION_TYPES.map((type) => (
                <option key={type} value={type}>
                  {DECISION_TYPE_LABEL[type]}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-primary" onClick={() => void handleCreateDecision()}>
              Agregar decisión
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
